import { Injectable } from '@angular/core';

import { lastValueFrom } from 'rxjs';

import {
    AuthorizationService,
    EntryPointService,
    JobService,
    JobSiteService,
    ModelConverterService,
    ProcessingInfo,
    ProjectService,
    WizardSummaryData,
} from '@mirage-projects/cw-commons';
import {
    ControllerDBUpload,
    FileExtended,
    PcvizCompanyReport,
    PcvizEstimationCostCredits,
    PcvizJobProcessData,
    PcvizUploadConfig,
    PcvizUploadConfigObject,
    Project,
    ProjectData,
} from '@mirage-projects/cw-models';
import { DataAccessPcvizService, DbPcvizService, JobPcvizService } from '@mirage-projects/cw-pcviz-core';
import { FilesHelperService } from '@mirage-projects/cw-utils';

import { AppProjectService } from '../app-project.service';

@Injectable({ providedIn: 'root' })
export class AppUploadService {
    private readonly controller: ControllerDBUpload = this.dbPcvizService.getControllerDB('Upload');

    constructor(
        private jobService: JobService,
        private dbPcvizService: DbPcvizService,
        private jobPcvizService: JobPcvizService,
        private projectService: ProjectService,
        private jobSiteService: JobSiteService,
        private entryPointService: EntryPointService,
        private filesHelperService: FilesHelperService,
        private authorizationService: AuthorizationService,
        private dataAccessPcvizService: DataAccessPcvizService,
        private appProjectService: AppProjectService,
        private modelConverterService: ModelConverterService
    ) {}

    /**
     * Upload and process project
     */
    async uploadAndProcessProject(
        config: PcvizUploadConfig,
        summary: Partial<WizardSummaryData>[],
        jobSiteId?: number
    ): Promise<Project | undefined> {
        const jobAdapter = this.jobPcvizService.getJobAdapter();
        const fileCurrentlyUploading: boolean = this.checkIsCurrentlyUploading();
        const areThereFiles = config.files?.length > 0;

        if (!jobAdapter || fileCurrentlyUploading || !areThereFiles) {
            return;
        }

        const projectData: ProjectData = {
            name: config.object?.name || null,
            projectType: jobAdapter._projectType,
            available: false,
            captureDate: config.object?.captureDate || undefined,
        };

        const response = await lastValueFrom(this.projectService.create(projectData));
        if (jobSiteId) {
            await lastValueFrom(this.jobSiteService.addProject(jobSiteId, response.id));
        }
        config.object = response as PcvizUploadConfigObject;
        config.output = this.entryPointService.getEntryPoint(jobAdapter.getObjectType(), (config as any).object.id);
        config.files.forEach((file: FileExtended) => {
            file.alias = this.filesHelperService.cleanUpFileNameCharacters(file.name);
        });

        try {
            await this.createAndUpload(config, undefined, true);
            const process = await this.jobPcvizService.convertLayer(config);

            this.jobService.startCheckProcessStatus(process, config);
        } catch (e: any) {
            if (e) {
                const firtsSummary = summary.find((item) => !!item);
                if (firtsSummary) {
                    firtsSummary.errors = firtsSummary.errors ?? [];
                    firtsSummary.errors.push(e);
                }
            }
        }
        return response;
    }

    /**
     * Upload and process layer
     */
    async uploadAndProcessLayer(config: PcvizUploadConfig): Promise<void> {
        if (this.checkIsCurrentlyUploading()) {
            return;
        }

        try {
            const process = await this.createAndProcessLayer(config);

            this.jobService.startCheckProcessStatus(process, config);
        } catch (e: any) {
            if (e) {
                throw new Error(e);
            }
        }
    }

    /**
     * creates and uploads the reference data for a job site
     * @param config
     */
    async createAndUploadJobSiteReferenceData(config: PcvizUploadConfig): Promise<void> {
        try {
            await this.jobPcvizService.prepareAndGetOrCreateLayers(null, config);
            await this.jobPcvizService.uploadJobSiteReferenceData(config);
        } catch (e: any) {
            throw new Error(e);
        }
    }

    /**
     * Gets the cost estimation for the job
     * @param projectId Project id
     * @param config Scans and options to calculate the estimation
     */
    async getContextCaptureEstimationCost(projectId: number, config: Partial<PcvizJobProcessData>): Promise<PcvizEstimationCostCredits> {
        const estimation = await lastValueFrom(this.appProjectService.processScanEstimate(projectId, config));

        const processingInfo = await this.getProcessingInfo();
        const availableCredits = processingInfo.processingQuota - processingInfo.processingCost;
        estimation.processingCredits = this.authorizationService.getSupportUser() ? 0 : estimation.processingCredits;

        return {
            availableCredits,
            estimation,
            creditsAfterProcessing: availableCredits - estimation.processingCredits,
        };
    }

    private async getProcessingInfo(): Promise<ProcessingInfo> {
        const report: PcvizCompanyReport = await this.dataAccessPcvizService.getDriver('da.collage').getReport('company');

        return {
            storageSize: report.company.storageSize,
            storageQuota: report.licences.maxstorage,
            processingCost: report.company.processingCost,
            processingQuota: report.licences.maxprocessing,
        };
    }

    private async createAndUpload(config: PcvizUploadConfig, project: any, onlyCreateUploadJob = false): Promise<void> {
        if (!onlyCreateUploadJob) {
            await this.jobPcvizService.prepareAndGetOrCreateLayers(project, config);
        }

        try {
            await this.jobPcvizService.createUploadJob(config);
        } catch (e: any) {
            if (e && config.upload) {
                config.upload.status = 'INCOMPLETE';
                this.controller.updateInLocalStorage(config.upload);
                throw new Error(e);
            }
        }
    }

    private async createAndProcessLayer(config: PcvizUploadConfig): Promise<any> {
        if (this.modelConverterService.isModel3dConversion()) {
            await this.jobPcvizService.uploadFilesByConfig(config);
            await this.jobPcvizService.uploadFiles(config);
            return await this.modelConverterService.callModelConverterEndpoint(config);
        }
        await this.createAndUpload(config, null);
        return await this.jobPcvizService.convertLayer(config);
    }

    private checkIsCurrentlyUploading(): boolean {
        const isCurrentlyUploadingInAnotherTab =
            this.controller.isCurrentlyUploading() && this.controller.getCurrentUploadingSessionId() !== window.sessionStorage.tabId;

        return isCurrentlyUploadingInAnotherTab;
    }
}
